import type {
  ApplicationWithStats,
  RailwayCostRange,
  RailwayCostsSummary,
  RailwayProject,
} from '@owl/shared';
import { useEffect, useMemo, useState } from 'react';
import type { ShellCtx } from '../components/Shell';
import { Kpi, formatBucketLabel, formatUsd } from '../components/charts/AiUsageBlocks';
import { api } from '../lib/api';

const RANGES: RailwayCostRange[] = ['1h', '6h', '24h', '7d', '30d', 'mtd'];

export function RailwayCosts({ ctx }: { ctx: ShellCtx }) {
  const refreshTick = ctx.refreshTick;
  const [range, setRange] = useState<RailwayCostRange>('24h');
  const [projectFilter, setProjectFilter] = useState<string>('');
  const [summary, setSummary] = useState<RailwayCostsSummary | null>(null);
  const [projects, setProjects] = useState<RailwayProject[]>([]);
  const [applications, setApplications] = useState<ApplicationWithStats[]>([]);
  const [error, setError] = useState<string | null>(null);

  async function loadSummary() {
    try {
      const s = await api.railwayCostsSummary({
        range,
        railwayProjectId: projectFilter || undefined,
      });
      setSummary(s);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load summary');
    }
  }

  async function loadProjects() {
    try {
      const list = await api.listRailwayProjects();
      setProjects(list);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load projects');
    }
  }

  async function loadApplications() {
    try {
      const { items } = await api.listApplications();
      setApplications(items);
    } catch {
      // non-fatal: mapping editor falls back to "no apps available"
    }
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: load* are stable closures, deps tracked manually
  useEffect(() => {
    loadSummary();
  }, [refreshTick, range, projectFilter]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: load* are stable closures, deps tracked manually
  useEffect(() => {
    loadProjects();
    loadApplications();
  }, [refreshTick]);

  const projectByRailwayId = useMemo(() => {
    const m = new Map<string, RailwayProject>();
    for (const p of projects) m.set(p.railwayProjectId, p);
    return m;
  }, [projects]);

  async function handleMappingChange(railwayProjectsRowId: string, applicationId: string | null) {
    try {
      await api.updateRailwayProject(railwayProjectsRowId, applicationId);
      await Promise.all([loadProjects(), loadSummary()]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update mapping');
    }
  }

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1 className="view-title" style={{ margin: 0 }}>
            Railway costs
          </h1>
          <div className="view-sub">
            Infrastructure usage and computed cost per Railway project. Captured every 30 minutes
            via the Railway GraphQL API.
          </div>
        </div>
        <div className="range-picker">
          {RANGES.map((r) => (
            <button
              key={r}
              type="button"
              className={`range-opt ${range === r ? 'active' : ''}`}
              onClick={() => setRange(r)}
            >
              {r === 'mtd' ? 'MTD' : r}
            </button>
          ))}
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          marginTop: 8,
          marginBottom: 16,
          fontSize: 13,
        }}
      >
        <label htmlFor="rc-project-filter" className="dim">
          Project
        </label>
        <select
          id="rc-project-filter"
          value={projectFilter}
          onChange={(e) => setProjectFilter(e.target.value)}
          style={{
            background: 'var(--bg-2)',
            border: '1px solid var(--border-1)',
            borderRadius: 8,
            color: 'var(--fg-1)',
            padding: '6px 10px',
            minWidth: 220,
          }}
        >
          <option value="">All projects</option>
          {projects.map((p) => (
            <option key={p.id} value={p.railwayProjectId}>
              {p.name}
              {p.deletedAtRailway ? ' [deleted]' : ''}
            </option>
          ))}
        </select>
      </div>

      {error && <div className="error-banner">{error}</div>}

      <SummaryPanel
        summary={summary}
        range={range}
        applications={applications}
        projectByRailwayId={projectByRailwayId}
        onMappingChange={handleMappingChange}
      />
    </div>
  );
}

function SummaryPanel({
  summary,
  range,
  applications,
  projectByRailwayId,
  onMappingChange,
}: {
  summary: RailwayCostsSummary | null;
  range: RailwayCostRange;
  applications: ApplicationWithStats[];
  projectByRailwayId: Map<string, RailwayProject>;
  onMappingChange: (railwayProjectsRowId: string, applicationId: string | null) => void;
}) {
  if (summary === null) {
    return <div className="empty-state">Loading…</div>;
  }
  const t = summary.totals;
  const maxBucket = Math.max(0, ...summary.timeline.map((b) => b.totalCostUsd));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div className="kpi-row">
        <Kpi label="Total cost" value={formatUsd(t.totalCostUsd)} />
        <Kpi label="CPU" value={formatUsd(t.cpuCostUsd)} />
        <Kpi label="Memory" value={formatUsd(t.memCostUsd)} />
        <Kpi label="Disk" value={formatUsd(t.diskCostUsd)} />
        <Kpi label="Network egress" value={formatUsd(t.networkCostUsd)} />
        <Kpi label="Samples" value={t.samples.toString()} />
      </div>

      <div className="table-wrap">
        <div className="view-sub" style={{ padding: '6px 4px 8px' }}>
          Timeline
        </div>
        {summary.timeline.length === 0 || maxBucket === 0 ? (
          <div className="dim" style={{ padding: 16, textAlign: 'center', fontSize: 13 }}>
            No samples in this range
          </div>
        ) : (
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-end',
              gap: 2,
              height: 120,
              padding: 8,
              background: 'var(--bg-2)',
              border: '1px solid var(--border-1)',
              borderRadius: 8,
            }}
          >
            {summary.timeline.map((b) => {
              const heightPct = maxBucket > 0 ? (b.totalCostUsd / maxBucket) * 100 : 0;
              return (
                <div
                  key={b.bucket}
                  title={`${formatBucketLabel(b.bucket, range)} · ${formatUsd(b.totalCostUsd)}`}
                  style={{
                    flex: 1,
                    minWidth: 2,
                    height: `${Math.max(heightPct, b.totalCostUsd > 0 ? 2 : 0)}%`,
                    background: 'var(--accent-1, #6ea8fe)',
                    borderRadius: 2,
                    transition: 'height 0.2s',
                  }}
                />
              );
            })}
          </div>
        )}
      </div>

      <div className="table-wrap">
        <div className="view-sub" style={{ padding: '6px 4px 8px' }}>
          By project
        </div>
        <table className="data-table">
          <thead>
            <tr>
              <th>Railway project</th>
              <th>OWL app</th>
              <th>CPU</th>
              <th>Memory</th>
              <th>Disk</th>
              <th>Network</th>
              <th>Total</th>
              <th>%</th>
            </tr>
          </thead>
          <tbody>
            {summary.byProject.length === 0 ? (
              <tr>
                <td colSpan={8} className="dim" style={{ padding: 16, textAlign: 'center' }}>
                  No samples in this range
                </td>
              </tr>
            ) : (
              summary.byProject.map((p) => {
                const owlProject = projectByRailwayId.get(p.railwayProjectId);
                const pct =
                  t.totalCostUsd > 0
                    ? `${((p.totalCostUsd / t.totalCostUsd) * 100).toFixed(1)}%`
                    : '—';
                return (
                  <tr key={p.railwayProjectId}>
                    <td>
                      <strong>{p.projectName}</strong>
                      {owlProject?.deletedAtRailway && (
                        <span className="dim" style={{ marginLeft: 8, fontSize: 11 }}>
                          [deleted]
                        </span>
                      )}
                    </td>
                    <td>
                      {owlProject ? (
                        <select
                          value={owlProject.applicationId ?? ''}
                          onChange={(e) => onMappingChange(owlProject.id, e.target.value || null)}
                          style={{
                            background: 'var(--bg-2)',
                            border: '1px solid var(--border-1)',
                            borderRadius: 6,
                            color: 'var(--fg-1)',
                            padding: '4px 8px',
                            fontSize: 12,
                            maxWidth: 180,
                          }}
                        >
                          <option value="">—</option>
                          {applications.map((a) => (
                            <option key={a.id} value={a.id}>
                              {a.name}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <span className="dim" style={{ fontSize: 11 }}>
                          (no project row)
                        </span>
                      )}
                    </td>
                    <td className="mono">{formatUsd(p.cpuCostUsd)}</td>
                    <td className="mono">{formatUsd(p.memCostUsd)}</td>
                    <td className="mono">{formatUsd(p.diskCostUsd)}</td>
                    <td className="mono">{formatUsd(p.networkCostUsd)}</td>
                    <td className="mono">
                      <strong>{formatUsd(p.totalCostUsd)}</strong>
                    </td>
                    <td className="mono dim">{pct}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
